import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {WidgetErrorBanner} from 'shells/card/card-widget/widget-error-banner';
import {EmailRow} from 'shells/row/email-row/email-row';

import type {PeepBusinessCardInfoFragment_emailAddresses_all as EmailAddress} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../../../store';
import type {UserFacingEntity} from '../../../../core-entity/rows/peep-rows/types';
import {
    openComposerWithEmailAddress,
    promptOverrideMinimizedEmail,
} from '../../../../email/email-composer-actions';
import {getWasMinimizedForEntityId} from '../../../../email/email-composer-selectors';

import {getTitleDropdownOptions} from '../helpers';
import type {PatchEmailInput, PatchTitleInput, RowErrorMessage} from '../types';

interface Props {
    entityId: string;
    emailAddresses: EmailAddress[] | null | undefined;
    canMarkAsPrimary: boolean;
    parentEntity: UserFacingEntity;
    labelOptions: string[] | null | undefined;
    newlyAddedId?: string | null;
    onUpdateEmail: (args: PatchEmailInput) => Promise<any>;
    onUpdateTitle: (args: PatchTitleInput) => Promise<any>;
    rowErrorMessage: RowErrorMessage | null;
    setRowErrorMessage: (rowErrorMessage: RowErrorMessage | null) => void;
    sendFromNutshellEnabled: boolean | null | undefined;
}

export function EmailRowList(props: Props) {
    const dispatch = useDispatch();
    const minimizedForEntityId = useSelector((state: NutshellState) =>
        getWasMinimizedForEntityId(state)
    );

    const openComposer = useCallback(
        (emailAddress: string) => {
            if (minimizedForEntityId && minimizedForEntityId !== props.entityId) {
                dispatch(promptOverrideMinimizedEmail(props.entityId, emailAddress));
            } else {
                dispatch(
                    openComposerWithEmailAddress({
                        emailAddress,
                        entityId: props.entityId,
                    })
                );
            }
        },
        [dispatch, minimizedForEntityId, props.entityId]
    );

    return (
        <>
            {props.emailAddresses?.map((emailAddress) => {
                const email = emailAddress.value?.address;
                if (!email) {
                    return null;
                }
                const errorMessage =
                    props.rowErrorMessage?.id === emailAddress.id
                        ? props.rowErrorMessage.errorMessage
                        : null;

                const dropdownOptions = getTitleDropdownOptions(
                    props.labelOptions,
                    emailAddress.id,
                    props.onUpdateTitle
                );

                return (
                    <div key={`${emailAddress.id}-emailAddress`}>
                        {errorMessage && (
                            <div className='pb-2 px-8'>
                                <WidgetErrorBanner
                                    error={errorMessage}
                                    setError={(error?: string | null) => {
                                        props.setRowErrorMessage(
                                            error
                                                ? {
                                                      id: emailAddress.id,
                                                      errorMessage: error,
                                                  }
                                                : null
                                        );
                                    }}
                                />
                            </div>
                        )}
                        <EmailRow
                            title={emailAddress.name}
                            email={email}
                            parentEntity={props.parentEntity}
                            isPrimary={emailAddress.isPrimary}
                            showIsPrimaryIcon={props.emailAddresses?.length !== 1}
                            isFormer={emailAddress.isFormer}
                            canMarkAsPrimary={props.canMarkAsPrimary}
                            dropdownOptions={dropdownOptions}
                            onUpdateEmail={(updatedEmail: string) =>
                                props.onUpdateEmail({
                                    attributeValueId: emailAddress.id,
                                    emailValue: updatedEmail,
                                })
                            }
                            onMarkAsPrimary={() =>
                                props.onUpdateEmail({
                                    attributeValueId: emailAddress.id,
                                    isPrimary: true,
                                })
                            }
                            onChangeFormerStatus={(isFormer: boolean) =>
                                props.onUpdateEmail({
                                    attributeValueId: emailAddress.id,
                                    isFormer: isFormer,
                                })
                            }
                            onDelete={() =>
                                props.onUpdateEmail({
                                    attributeValueId: emailAddress.id,
                                    isDelete: true,
                                })
                            }
                            onUpdateTitle={(title: string) =>
                                props.onUpdateTitle({
                                    attributeValueId: emailAddress.id,
                                    name: title,
                                })
                            }
                            connectButtonsProps={{
                                entityId: props.entityId,
                                sendFromNutshellEnabled: props.sendFromNutshellEnabled,
                                onOpenEmailModal: (emailAddress: string) =>
                                    openComposer(emailAddress),
                                deliverabilityStatus: emailAddress.deliverabilityStatus,
                            }}
                            successFlashKey={
                                props.newlyAddedId === emailAddress.id ? emailAddress.id : undefined
                            }
                        />
                    </div>
                );
            })}
        </>
    );
}
